import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../context/OnboardingContext';
import OnboardingLayout, { CancelButton } from './OnboardingLayout';
import './onboarding.css';
import { ServiceDefinitionService, ServiceDefinition } from '../../services/service-definition.service';

// Interfaz para los servicios seleccionados
interface SelectedService {
  id: string;
  name: string;
}

const ServiceSelection: React.FC = () => {
  const navigate = useNavigate();
  const { companyData, updateCompanyData, setCurrentStep } = useOnboarding();
  const [selectedServices, setSelectedServices] = useState<SelectedService[]>([]);
  const [availableServices, setAvailableServices] = useState<ServiceDefinition[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Si no hay categoría seleccionada, redirigir a la selección de categoría
    if (!companyData.category) {
      navigate('/onboarding/category');
      return;
    }

    // Obtener los servicios disponibles para la subcategoría seleccionada desde el backend
    const fetchServices = async () => {
      try {
        setLoading(true);

        // Obtener el ID de la categoría desde localStorage
        const categoryId = localStorage.getItem('selectedCategoryId');

        if (categoryId) {
          const services = await ServiceDefinitionService.getServicesBySubcategory(categoryId);
          setAvailableServices(services);
          setError(null);
        } else {
          setError('No se ha seleccionado una categoría correctamente.');
        }
      } catch (err) {
        setError('No se pudieron cargar los servicios. Por favor, intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [companyData.category, navigate]);

  const handleServiceToggle = (service: ServiceDefinition) => {
    setSelectedServices(prev => {
      const isSelected = prev.some(s => s.id === service.id);
      if (isSelected) {
        return prev.filter(s => s.id !== service.id);
      } else {
        return [...prev, { id: service.id, name: service.name }];
      }
    });
  };

  const handleContinue = () => {
    if (selectedServices.length > 0) {
      // Extraer solo los nombres de los servicios para cumplir con el tipo string[] esperado por el contexto
      const serviceNames = selectedServices.map(service => service.name);
      updateCompanyData({ services: serviceNames });
      setCurrentStep(3); // Avanzar al siguiente paso
      navigate('/onboarding/profile-documents'); // Navegar a la página de perfil y documentos
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
    navigate('/onboarding/category');
  };

  // Dividir los servicios en dos columnas
  const midPoint = Math.ceil(availableServices.length / 2);
  const leftColumnServices = availableServices.slice(0, midPoint);
  const rightColumnServices = availableServices.slice(midPoint);

  return (
    <OnboardingLayout
      title="¡Activa tu magia donde haces la diferencia!"
      subtitle="Cuéntanos en qué servicios eres experto. Esta información nos permitirá conectarte con las personas que más necesitan lo que tú sabes hacer."
      showBackButton={true}
      onBack={handleBack}
    >
      {/* Encabezado de servicios con contador */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark">Selecciona tus servicios</h3>
        <span className="text-sm text-xervix-primary px-3  rounded-full font-semibold">
          {selectedServices.length} seleccionados
        </span>
      </div>

      {/* Contenedor con scroll para los servicios */}
      <div className="md:h-[200px] h-[400px] overflow-y-auto mb-8 bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark p-6 rounded-lg">
        {loading ? (
          // Mostrar esqueletos de carga mientras se cargan los servicios
          <div className="grid grid-cols-1 md:grid-cols-2 md:gap-6">
            {Array.from({ length: 10 }).map((_, index) => (
              <div key={index} className="mb-4 flex items-center animate-pulse">
                <div className="h-4 w-4 bg-gray-300 dark:bg-gray-700 rounded mr-3"></div>
                <div className="h-4 w-40 bg-gray-300 dark:bg-gray-700 rounded"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          // Mostrar mensaje de error
          <div className="text-center py-8">
            <p className="text-red-500">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-xervix-primary text-white rounded-md hover:bg-opacity-90"
            >
              Reintentar
            </button>
          </div>
        ) : availableServices.length === 0 ? (
          // Mostrar mensaje si no hay servicios disponibles
          <div className="text-center py-8">
            <p className="text-xervix-text-primary dark:text-xervix-text-primary-dark">
              No hay servicios disponibles para esta categoría.
            </p>
          </div>
        ) : (
          // Mostrar servicios
          <div className="grid grid-cols-1 md:grid-cols-2 md:gap-6">
            {/* Columna izquierda */}
            <div>
              {leftColumnServices.map((service) => (
                <div key={service.id} className="mb-4 flex items-center">
                  <label className="flex items-center cursor-pointer w-full">
                    <input
                      type="checkbox"
                      checked={selectedServices.some(s => s.id === service.id)}
                      onChange={() => handleServiceToggle(service)}
                      className="form-checkbox"
                    />
                    <span className="ml-3 text-xervix-text-primary dark:text-xervix-text-primary-dark">
                      {service.name}
                    </span>
                  </label>
                </div>
              ))}
            </div>

            {/* Columna derecha */}
            <div>
              {rightColumnServices.map((service) => (
                <div key={service.id} className="mb-4 flex items-center">
                  <label className="flex items-center cursor-pointer w-full">
                    <input
                      type="checkbox"
                      checked={selectedServices.some(s => s.id === service.id)}
                      onChange={() => handleServiceToggle(service)}
                      className="form-checkbox"
                    />
                    <span className="ml-3 text-xervix-text-primary dark:text-xervix-text-primary-dark">
                      {service.name}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Botones de acción */}
      <div className="flex justify-center gap-6">
        <CancelButton />
        <button
          onClick={handleContinue}
          disabled={selectedServices.length === 0 || loading}
          className={`
            px-10 py-3 rounded-md text-white font-medium transition-colors
            ${selectedServices.length > 0 && !loading
              ? 'bg-xervix-primary hover:bg-opacity-90'
              : 'bg-gray-400 cursor-not-allowed'
            }
          `}
        >
          {loading ? 'Cargando...' : 'Continuar'}
        </button>
      </div>
    </OnboardingLayout>
  );
};

export default ServiceSelection;
